import { useApiMutation } from '@/hooks/useApiMutation';
import { api } from '@/lib/api-client';
import { ConnectedSolanaWallet, useSolanaWallets } from '@privy-io/react-auth/solana';
import { PublicKey, Transaction } from '@solana/web3.js';
import { useCallback, useState } from 'react';

export type PaymentToken = 'SOL' | 'USDC' | 'ANTIRUG';

interface UseSolanaPaymentResult {
  pay: (amount: number, token: PaymentToken) => Promise<string | undefined>;
  status: 'idle' | 'pending' | 'success' | 'error';
  error: Error | null;
  signature: string | null;
}

const hasSignTransaction = (wallet: ConnectedSolanaWallet) => {
  return wallet.address && wallet.signTransaction;
};

export function useSolanaPayment(): UseSolanaPaymentResult {
  const { wallets } = useSolanaWallets();
  const [status, setStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
  const [error, setError] = useState<Error | null>(null);
  const [signature, setSignature] = useState<string | null>(null);

  // Send transaction mutation
  const {
    mutateAsync: sendTransaction,
    isPending: isSending,
    error: sendError,
  } = useApiMutation((signedTx: string) => api.solanaSendTransaction({ signedTx }));

  const pay = useCallback(
    async (amount: number, token: PaymentToken) => {
      setStatus('pending');
      setError(null);
      setSignature(null);
      try {
        const solWallet = wallets.find(hasSignTransaction);
        if (!solWallet) throw new Error('No connected Solana wallet');
        const publicKey = new PublicKey(solWallet.address);

        // 1. Request unsigned tx from backend using API standardization
        const { tx } = await api.solanaCreatePaymentTx({
          payer: publicKey.toBase58(),
          amount,
          token,
        });
        if (!tx) throw new Error('Failed to create payment transaction');

        // 2. Sign with wallet
        const unsignedTx = Buffer.from(tx, 'base64');
        const transaction = await solWallet.signTransaction(Transaction.from(unsignedTx));
        const signedTx = Buffer.from(transaction.serialize()).toString('base64');

        // 3. Broadcast signed tx
        const sendData = await sendTransaction(signedTx);
        setSignature(sendData.txid);
        setStatus('success');
        return sendData.txid;
      } catch (err) {
        setError(err as Error);
        setStatus('error');
        return undefined;
      }
    },
    [wallets, sendTransaction],
  );

  return {
    pay,
    status: isSending ? 'pending' : status,
    error: error || sendError,
    signature,
  };
}
